import Mustache from 'mustache';
import elevatedContainer from '../../generic/elevatedContainer';
import FormV10 from '../../v10/form/form-v10'
import analytics from '../../analytics';
import {getToken} from '../../cmtoken';

/**
 * Modal form class. This class is used to show a modal with a form.
 */
export default class ModalForm {

  /**
   * @type {Object}
   */
  #dom = {}

  /**
   * @constructor
   * @param {HTMLFormElement} element form element
   */
  constructor(element) {
    this.#findElements(element);
    this.#addEventListeners();
  }

  /**
   * Find all used elements in the form for a later use.
   *
   * @param {HTMLFormElement} element element to where to bind info toolip logic
   * @throws {Error} if no form element is found from container element.
   */
  #findElements(element) {
    this.#dom.element = element;
    this.#dom.form = element.querySelector('form');
    this.#dom.thankYouPage = element.querySelector('.thankyoupage');

    if(!(this.#dom.form instanceof HTMLFormElement)) {
      throw new Error('Is expected that container have at least one instance of HTMLFormElement. None was found =/');
    }
  }

  /**
   * Add basic event listeners to form.
   */
  #addEventListeners() {
    this.#dom.form.addEventListener('submit', this.#onSubmit.bind(this))
  }

  /**
   * Overwrite normal form submit event and send the form instead to ajax endpoint
   * by using a fetch request. Response is handled and validation errors are highlighted.
   *
   * @param {SubmitEvent} event event object
   */
  async #onSubmit(event) {
    event.preventDefault();
    const token = getToken();

    const data = {};
    new FormData(this.#dom.form).forEach((value, key) => data[key] = value);

    try {
      const response = await fetch( this.#dom.form.action, {
        method: 'POST',
        body: JSON.stringify({ ...data, token })
      });

      const responseJson = await response.json();

      if( responseJson.action === 'show-errors' ) {
        // Highlight validation errors and successes in the form.
        FormV10.showFieldValidation( this.#dom.form, responseJson.validation.errors, responseJson.validation.success );
      }

      if( responseJson.action === 'show-thankyoupage' ) {
        this.#dom.form.classList.add('hidden');
        this.#dom.thankYouPage.classList.remove('hidden');

        // If analytics interaction name exists in modal
        // Send event to dataLayer
        if(this.#dom.form.dataset?.uiInteractionName) {
          analytics.pushUIInteractionAnalytics({
            element: 'button',
            elementName: this.#dom.form.dataset.uiInteractionName,
            type: 'click',
          });
        }
      }
    } catch( error ) {
      console.warn('ModalForm submit error', error)
    }
  }

  /**
   * Check if given modal target is one of the supported modal types.
   * @param {string} target target modal
   * @returns {boolean} true if given modal target is supported, otherwise false.
   */
  static isSupportedModal( target ) {
    let errors = [];
    const supportedModalTypes = ['raffleformmodal'];

    if( supportedModalTypes.includes(target) ) {
      const modalTemplate = document.querySelector(`#${target}-template`);
      if( !modalTemplate) {
        errors.push(`${target} - Supported modal template is not found from DOM`)
      }
    } else {
      errors.push(`Given modal target '${target}' is not a supported modal type. Supported types are ${supportedModalTypes}`)
    }

    if( errors.length > 0 ) {
      console.log( errors );
    }

    return errors.length === 0;
  }

  /**
   * Show modal with a form.
   *
   * @param {string} target target modal
   */
  static showModal( target ) {
    const currentModal = document.getElementById(target);
    if( ModalForm.isSupportedModal(target) && !currentModal ) {
      const modalTemplate = document.querySelector(`#${target}-template`);

      try {
        const templatesAndData = JSON.parse( modalTemplate.innerHTML );
        const html = Mustache.render(templatesAndData.template, templatesAndData.data, { ...templatesAndData.partials });
        document.body.insertAdjacentHTML('afterbegin', html);

        const element = document.querySelector(`#${templatesAndData.data.id}`);
        elevatedContainer.toggleVisibility(element, true);
        elevatedContainer.initToggleElements(element);

        const container = document.querySelector(`#${templatesAndData.data.id}`);

        if( ModalForm ) {
          new ModalForm( container );
        } else {
          console.error('No HTMLFormElement found inside elavated container element.')
        }
      } catch( error ) {
        console.log( 'Problems showing a modal', error );
      }
    } else {
      elevatedContainer.toggleVisibility(currentModal, true);
    }
  }
}
